import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import css from "./ClampedActionContent.module.scss";

const DEFAULT_LINES = 4;

// Quill (bubble theme) and TipTap each set their own line-height on the text
// element, so read it there instead of assuming the wrapper's value.
const getTextElement = (wrapper) =>
  wrapper.querySelector(".ql-editor, .ProseMirror") || wrapper;

const getLineHeight = (element) => {
  const style = window.getComputedStyle(element);
  const lineHeight = parseFloat(style.lineHeight);

  if (!Number.isNaN(lineHeight)) return lineHeight;
  return parseFloat(style.fontSize) * 1.4;
};

/**
 * Collapses long action-item text to `lines` and shows a "Read more" toggle.
 * The toggle only appears when the content actually overflows.
 */
const ClampedActionContent = ({
  lines = DEFAULT_LINES,
  expandOnFocus = false,
  children,
}) => {
  const contentRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState(null);
  const [overflowing, setOverflowing] = useState(false);

  const measure = useCallback(() => {
    const content = contentRef.current;
    if (!content) return;

    const height = getLineHeight(getTextElement(content)) * lines;
    setCollapsedHeight(height);
    // scrollHeight is the full content height even while the clamp is applied.
    setOverflowing(content.scrollHeight > height + 1);
  }, [lines]);

  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    const child = contentRef.current?.firstElementChild;
    if (!child || typeof ResizeObserver === "undefined") return;

    // The child is never clamped, so it reports both content and width changes.
    const observer = new ResizeObserver(measure);
    observer.observe(child);
    return () => observer.disconnect();
  }, [measure]);

  const clamped = !expanded && overflowing && collapsedHeight !== null;

  return (
    <div className={css.wrapper}>
      <div
        ref={contentRef}
        className={css.content}
        style={clamped ? { maxHeight: collapsedHeight } : undefined}
        onFocusCapture={expandOnFocus ? () => setExpanded(true) : undefined}
      >
        {children}
      </div>

      {overflowing && (
        <button
          type="button"
          className={css.toggle}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
};

export default ClampedActionContent;
